import React, { useEffect, useState, useMemo } from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { RectShape } from 'react-placeholder/lib/placeholders';
import { useSelector, useDispatch } from 'react-redux';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import Dropdown from 'commons/src/components/dropdown/MultipleAttrDropdown';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { colors } from 'commons/src/constants';
import { iaqInsight2ReportSensors } from 'commons/src/DeviceAndSensorLists';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { fetchBuilding } from '../../../../../actions/locationActions';
import { fetchSpaces } from '../../../../../actions/spaceActions';
import { DateRangeSelector } from '../../../../../components/DateRangeSelector';
import { SensorMultiSelect } from '../../../../../components/SensorMultiSelect';
import { Space } from '../../../../../models/spaceModels';
import { Store } from '../../../../../reducers';
import { BusinessRequestType } from '../../../../../reducers/BusinessRequestType';
import styles from '../../iaqInsight2.module.scss';
import SpaceSelectionModal from './SpaceSelectionModal';

export type SpaceSelectionType = 'ALL' | 'ISSUES' | 'CUSTOM';

export type ReportFormData = {
    from: string;
    to: string;
    locationId: string;
    spaces: {
        type: SpaceSelectionType;
        spaceIds: string[];
    };
    sensors: string[];
    hideCharts?: boolean;
    includeLogo?: boolean;
};

export type Props = {
    onGenerateReport: (data: ReportFormData) => void;
    loading: boolean;
    reportStartTime: Moment | null;
    reportEndTime: Moment | null;
    setReportStartTime: (date: Moment | null) => void;
    setReportEndTime: (date: Moment | null) => void;
    rightPanelContent?: React.ReactNode;
};

const ReportForm: React.FC<Props> = ({
    onGenerateReport,
    loading,
    reportStartTime,
    reportEndTime,
    setReportStartTime,
    setReportEndTime,
    rightPanelContent,
}) => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();

    const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
    const [selectedBuildingName, setSelectedBuildingName] = useState<string>('');
    const [spaceSelectionType, setSpaceSelectionType] = useState<SpaceSelectionType>('ALL');
    const [customSpaceIds, setCustomSpaceIds] = useState<string[]>([]);
    const [selectedSensors, setSelectedSensors] = useState<SensorTypes[]>([]);
    const [includeLogo, setIncludeLogo] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [datesAreValid, setDatesAreValid] = useState(false);
    const [displayValidation, setDisplayValidation] = useState(false);

    const locations = useSelector((state: Store) => state.locations.locations);
    const buildings = useSelector((state: Store) => state.buildings.buildings);
    const locationsLoading = useSelector((state: Store) => state.locations.loading);
    const fetchBuildingLoading = useSelector(
        (state: Store) => state.requests[BusinessRequestType.FetchBuilding]?.loading || false
    );
    const spaces = useSelector((state: Store) => state.spaces.spaces);

    const building = useMemo(() => {
        return selectedBuildingId ? buildings[selectedBuildingId] : undefined;
    }, [selectedBuildingId, buildings]);

    const buildingSpaces = useMemo(() => {
        if (!selectedBuildingId || !spaces[selectedBuildingId]) return [];
        const locationSpaces = spaces[selectedBuildingId].spaces || [];
        return locationSpaces.filter((space: Space) => space.placement === 'ROOM');
    }, [selectedBuildingId, spaces]);

    useEffect(() => {
        if (!selectedBuildingId) {
            setSpaceSelectionType('ALL');
            setCustomSpaceIds([]);
            setSelectedSensors([]);
            setReportStartTime(null);
            setReportEndTime(null);
            setDatesAreValid(false);
        }
    }, [selectedBuildingId, setReportStartTime, setReportEndTime]);

    const locationOptions = useMemo(() => {
        return locations.map((location: { id: string; name: string }) => ({
            id: location.id,
            inputValue: location.name,
        }));
    }, [locations]);

    const spaceOptions = useMemo(
        () => [
            { id: 'ALL', inputValue: txt('Spaces.AllSpaces') },
            { id: 'CUSTOM', inputValue: txt('IAQReport2.CustomSelection') },
        ],
        [txt]
    );

    const spaceSelectionDisplayValue = useMemo(() => {
        if (!building) {
            return null;
        }
        const selectedOption = spaceOptions.find(opt => opt.id === spaceSelectionType);
        return selectedOption?.inputValue || null;
    }, [building, spaceSelectionType, spaceOptions]);

    useEffect(() => {
        if (selectedBuildingId) {
            dispatch(fetchSpaces(selectedBuildingId));
            dispatch(fetchBuilding(selectedBuildingId));
        }
    }, [selectedBuildingId, dispatch]);

    useEffect(() => {
        if (selectedSensors.length === 0 && building) {
            setSelectedSensors([SensorTypes.co2, SensorTypes.temp, SensorTypes.humidity]);
        }
    }, [building, selectedSensors.length]);

    const handleBuildingSelect = (option: { id: string; inputValue: string }): void => {
        setSelectedBuildingId(option.id);
        setSelectedBuildingName(option.inputValue);
        setSpaceSelectionType('ALL');
        setCustomSpaceIds([]);
    };

    const handleSpaceSelectionChange = (option: { id: string; inputValue: string }): void => {
        const newType = option.id as SpaceSelectionType;
        setSpaceSelectionType(newType);
        if (newType !== 'CUSTOM') {
            setCustomSpaceIds([]);
        } else if (newType === 'CUSTOM' && customSpaceIds.length === 0) {
            setIsCustomModalOpen(true);
        }
    };

    const handleCustomSpacesSave = (spaceIds: string[]): void => {
        setCustomSpaceIds(spaceIds);
        // If all spaces are selected, switch to ALL
        if (spaceIds.length === buildingSpaces.length && buildingSpaces.length > 0) {
            setSpaceSelectionType('ALL');
            setCustomSpaceIds([]);
        }
        // If the number of selected spaces doesn't match all available spaces, switch to CUSTOM
        else if (spaceSelectionType === 'ALL' && spaceIds.length < buildingSpaces.length) {
            setSpaceSelectionType('CUSTOM');
        }
    };

    const daysDifference = useMemo(() => {
        if (!reportStartTime || !reportEndTime) return 0;
        return reportEndTime.diff(reportStartTime, 'days') + 1;
    }, [reportStartTime, reportEndTime]);

    const shouldHideCharts = daysDifference > 31;
    const exceedsMaxDays = !!(reportStartTime && reportEndTime && daysDifference > 365);

    const canGenerateReport =
        building &&
        reportStartTime &&
        reportEndTime &&
        datesAreValid &&
        selectedSensors.length > 0 &&
        (spaceSelectionType !== 'CUSTOM' || customSpaceIds.length > 0);

    const handleGenerateReport = (): void => {
        if (
            !building ||
            !reportStartTime ||
            !reportEndTime ||
            !datesAreValid ||
            selectedSensors.length === 0 ||
            (spaceSelectionType === 'CUSTOM' && customSpaceIds.length === 0)
        ) {
            setDisplayValidation(true);
            return;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const formData: ReportFormData = {
            from: reportStartTime.format('YYYY-MM-DD'),
            to: reportEndTime.format('YYYY-MM-DD'),
            locationId: selectedBuildingId,
            spaces: {
                type: spaceSelectionType,
                spaceIds: spaceSelectionType === 'CUSTOM' ? customSpaceIds : [],
            },
            sensors: selectedSensors,
            hideCharts: shouldHideCharts,
            includeLogo,
        };
        onGenerateReport(formData);
    };

    const getRightPanelContent = (): React.ReactNode => {
        if (rightPanelContent) {
            return rightPanelContent;
        }
        if (locationsLoading && locations.length === 0) {
            return null;
        }
        if (selectedBuildingId) {
            return (
                <div className={styles.placeholder}>
                    <div className={styles.placeholderIcon}>
                        <MaterialIcon name="description" />
                    </div>
                    <p className={styles.placeholderText}>
                        {txt('IAQReport2.CompleteFormToGenerate')}
                        <br />
                        {txt('IAQReport2.YourIAQReport')}
                    </p>
                </div>
            );
        }
        return (
            <div className={styles.placeholder}>
                <div className={styles.placeholderIcon}>
                    <MaterialIcon name="description" />
                </div>
                <p className={styles.placeholderText}>{txt('IAQReport2.SelectBuildingToStart')}</p>
            </div>
        );
    };

    return (
        <div className={styles.formContainer}>
            <div className={styles.formLayout}>
                <div className={styles.leftPanel}>
                    <div className={styles.buildingAndSpacesRow}>
                        <div className={styles.formSection}>
                            <h3 className={styles.sectionTitle}>{txt('Building.Building')}</h3>
                            {locationsLoading || locationOptions.length === 0 ? (
                                <div className={styles.fieldSkeleton}>
                                    <RectShape
                                        color={colors.greyAsh}
                                        style={{
                                            width: '100%',
                                            height: '46px',
                                            borderRadius: '10px',
                                            marginBottom: '10px',
                                        }}
                                    />
                                </div>
                            ) : (
                                <Dropdown
                                    id="buildingSelector"
                                    title=""
                                    options={locationOptions}
                                    loading={locationsLoading}
                                    validate={displayValidation && !building}
                                    defaultOption={txt('Building.SelectBuilding')}
                                    value={building?.name || selectedBuildingName || null}
                                    onSelect={handleBuildingSelect}
                                    testAttr="select-building"
                                    testId="select-building"
                                    fullWidth
                                    disabled={fetchBuildingLoading}
                                />
                            )}
                        </div>

                        <div className={styles.formSection}>
                            <h3 className={styles.sectionTitle}>{txt('Spaces.Spaces')}</h3>
                            {(fetchBuildingLoading && !building) || locationsLoading || locationOptions.length === 0 ? (
                                <div className={styles.fieldSkeleton}>
                                    <RectShape
                                        color={colors.greyAsh}
                                        style={{
                                            width: '100%',
                                            height: '46px',
                                            borderRadius: '10px',
                                            marginBottom: '10px',
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    <Dropdown
                                        key={`spaces-selector-${spaceSelectionType}`}
                                        id="spacesSelector"
                                        title=""
                                        options={spaceOptions}
                                        loading={false}
                                        validate={false}
                                        defaultOption={building ? txt('Spaces.AllSpaces') : ''}
                                        value={spaceSelectionDisplayValue}
                                        onSelect={handleSpaceSelectionChange}
                                        testAttr="select-spaces"
                                        testId="select-spaces"
                                        fullWidth
                                        disabled={!building}
                                    />
                                    {building && (spaceSelectionType === 'ALL' || spaceSelectionType === 'CUSTOM') && (
                                        <div className={styles.customSelectionInfo}>
                                            <span className={styles.spaceCount}>
                                                {spaceSelectionType === 'ALL'
                                                    ? buildingSpaces.length
                                                    : customSpaceIds.length}{' '}
                                                {(spaceSelectionType === 'ALL'
                                                    ? buildingSpaces.length
                                                    : customSpaceIds.length) === 1
                                                    ? txt('IAQReport2.SpaceSelected')
                                                    : txt('IAQReport2.SpacesSelected')}
                                            </span>
                                            <button
                                                type="button"
                                                className={styles.editLink}
                                                onClick={(): void => {
                                                    if (spaceSelectionType === 'ALL') {
                                                        setCustomSpaceIds(buildingSpaces.map((s: Space) => s.id));
                                                    }
                                                    setIsCustomModalOpen(true);
                                                }}
                                            >
                                                <MaterialIcon name="edit" extraClass={styles.editIcon} />
                                                {txt('Edit')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {(fetchBuildingLoading && !building) || locationsLoading || locationOptions.length === 0 ? (
                        <>
                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>{txt('Sensors')}</h3>
                                <div className={styles.fieldSkeleton}>
                                    <RectShape
                                        color={colors.greyAsh}
                                        style={{
                                            width: '100%',
                                            height: '46px',
                                            borderRadius: '10px',
                                            marginBottom: '10px',
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>{txt('Insight.TimePeriod')}</h3>
                                <div className={styles.fieldSkeleton} style={{ display: 'flex', gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                        <RectShape
                                            color={colors.greyAsh}
                                            style={{
                                                width: '80px',
                                                height: '14px',
                                                marginBottom: '8px',
                                                borderRadius: '4px',
                                            }}
                                        />
                                        <RectShape
                                            color={colors.greyAsh}
                                            style={{ width: '100%', height: '46px', borderRadius: '10px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <RectShape
                                            color={colors.greyAsh}
                                            style={{
                                                width: '80px',
                                                height: '14px',
                                                marginBottom: '8px',
                                                borderRadius: '4px',
                                            }}
                                        />
                                        <RectShape
                                            color={colors.greyAsh}
                                            style={{ width: '100%', height: '46px', borderRadius: '10px' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formSection}>
                                <div className={styles.fieldSkeleton}>
                                    <RectShape
                                        color={colors.greyAsh}
                                        style={{ width: '100%', height: '46px', borderRadius: '8px' }}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>{txt('Sensors')}</h3>
                                <SensorMultiSelect
                                    sensors={iaqInsight2ReportSensors}
                                    selectedSensors={selectedSensors}
                                    onSelectionChange={setSelectedSensors}
                                    disabled={!building}
                                />
                            </div>

                            <div className={styles.formSection}>
                                <label htmlFor="includeLogo" className={styles.checkboxLabel}>
                                    <input
                                        id="includeLogo"
                                        type="checkbox"
                                        checked={includeLogo}
                                        onChange={(e): void => setIncludeLogo(e.target.checked)}
                                        disabled={!building}
                                        className={styles.checkbox}
                                    />
                                    <span className={styles.checkboxText}>{txt('IAQReport2.IncludeCompanyLogo')}</span>
                                </label>
                            </div>

                            <div className={styles.formSection}>
                                <h3 className={styles.sectionTitle}>{txt('Insight.TimePeriod')}</h3>
                                <div className={styles.timePeriodFields}>
                                    <DateRangeSelector
                                        setEndDate={setReportEndTime}
                                        setStartDate={setReportStartTime}
                                        startDate={reportStartTime}
                                        endDate={reportEndTime}
                                        minNumberOfDays={0}
                                        maxNumberOfDays={365}
                                        setDatesAreValid={setDatesAreValid}
                                        validateSelection={displayValidation || !!(reportStartTime && reportEndTime)}
                                        disabled={!building}
                                        hideFieldLabels
                                        suppressMaxDaysInlineHint
                                    />
                                </div>
                            </div>

                            {exceedsMaxDays && (
                                <div className={styles.formSection}>
                                    <div className={`${styles.warningMessage} ${styles.warningMessageRed}`}>
                                        <div className={styles.warningFirstRow}>
                                            <MaterialIcon name="warning" extraClass={styles.warningIcon} />
                                            <div className={styles.warningTitle}>
                                                {txt('IAQReport2.TimePeriodLimitExceeded')}
                                            </div>
                                        </div>
                                        <div className={styles.warningDescription}>
                                            {txt('Insight.MaxDaysHint', { maxDays: '365' })}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {shouldHideCharts && (
                                <div className={styles.formSection}>
                                    <div className={styles.warningMessage}>
                                        <div className={styles.warningFirstRow}>
                                            <MaterialIcon name="info" extraClass={styles.warningIcon} />
                                            <div className={styles.warningTitle}>
                                                {txt('IAQReport2.GraphWillNotBeDisplayed')}
                                            </div>
                                        </div>
                                        <div className={styles.warningDescription}>
                                            {txt('IAQReport2.GraphWillNotBeDisplayedDescription')}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={styles.formSection}>
                                <div className={styles.generateButtonWrapper}>
                                    <PrimaryButton
                                        title={txt('GenerateReport')}
                                        onClick={handleGenerateReport}
                                        loading={loading}
                                        disabled={!canGenerateReport}
                                        color={canGenerateReport ? 'primary' : 'secondary'}
                                        fullWidth
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.rightPanel}>{getRightPanelContent()}</div>
            </div>

            {isCustomModalOpen && building && (
                <SpaceSelectionModal
                    isOpen={isCustomModalOpen}
                    onClose={(): void => setIsCustomModalOpen(false)}
                    selectedSpaceIds={customSpaceIds}
                    onSave={handleCustomSpacesSave}
                    locationId={selectedBuildingId}
                />
            )}
        </div>
    );
};

export default ReportForm;
